// app.service.js
import config from 'app/config';

class AppService {

  // Ensure all dependencies are correctly injected for minification
  static $inject = ["$location", "$window", "$rootScope", "$httpParamSerializer"];

  /** @ngInject */
  constructor($location, $window, $rootScope, $httpParamSerializer) {
    Object.assign(this, {
      $location, $window, $rootScope, $httpParamSerializer
    });

    this.config = config;
  }

  // Check key in object
  hasProperty(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  // Return key value from object
  getProperty(object, key) {
    return this.hasProperty(object, key) ? object[key] : "";
  }

  // Query Params: read all the query params and return as object
  getQueryParamObject() {
    const urlSearchParams = new URLSearchParams(this.$window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    return params;
  }

  // Query Params: read all the query params and return as string
  getQueryParam() {
    const param = this.$window.location.search.substring(1);
    return param;
  }

  // Set URL to redirect with query param attached to it
  cta(url) {
    const noActionUrls = ['NA', 'hsevent', 'optin', undefined];
    if (!noActionUrls.includes(url)) { // Use .includes() for array check
      let qparams = this.getQueryParamObject();
      // Use angular.extend for merging, as it's an Angular context.
      // Alternatively, for plain objects, ES6 spread syntax ({...qparams, ...this.queryStringToJSON(url)}) could be used.
      qparams = this.$httpParamSerializer(angular.extend(qparams, this.queryStringToJSON(url)));
      if (qparams !== '') {
        url = `${url.split('?')[0]}?${qparams}`; // Use template literal
      }
    }
    return url;
  }

  // Query Params: append a param and return as string
  appendParam(key, value) {
    const params = new URLSearchParams(this.getQueryParam());
    params.append(key, value);
    return params.toString();
  }

  queryStringToJSON(str) {
    let pairs = [];
    const result = {};
    if (str.includes('?')) { // Use .includes()
      pairs = str.slice(str.indexOf('?') + 1).split('&');
    }
    if (pairs.length > 0) {
      pairs.forEach((pair) => {
        const parts = pair.split('='); // Use const
        const name = parts[0];
        const value = parts[1] || ''; // Default to empty string if value is undefined

        if (name.length) {
          if (result[name] !== undefined) {
            if (!Array.isArray(result[name])) { // Use Array.isArray for robust check
              result[name] = [result[name]];
            }
            result[name].push(value);
          } else {
            result[name] = value;
          }
        }
      });
    }
    return result;
  }

  // Query Params: read all the query param and return as object
  getUserQueryParam() {
    const qparams = this.getQueryParamObject();
    const userinfo = {};

    // Using Object.keys().forEach() as a modern alternative to angular.forEach
    // Note: The original angular.forEach with 'userinfo' as context might modify 'this' inside the callback.
    // Assuming the intent was to populate 'userinfo'.
    Object.keys({ 'email': '', 'firstname': '', 'fname': '', 'lastname': '', 'lname': '' }).forEach((key) => {
      if (this.hasProperty(qparams, key) && qparams[key] !== '') {
        userinfo[key] = qparams[key]; // Assign to userinfo object
      }
    });
    return this.$httpParamSerializer(userinfo);
  }

  // Browser: detect the client browser and return the browser agent
  browser() {
    const userAgent = this.$window.navigator.userAgent; // Use const
    const browsers = { chrome: /chrome/i, safari: /safari/i, firefox: /firefox/i, ie: /internet explorer/i }; // Use const

    for (const key in browsers) { // Use const in for...in
      if (browsers.hasOwnProperty(key) && browsers[key].test(userAgent)) { // Add hasOwnProperty check
        return key;
      }
    }
    return 'unknown';
  }

  // Mobile: detect the mobile device and return true if mobile
  // isMobile() {
  //   var check = false;
  //   (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
  //   return check;
  // }

  // Get GA client to bind in the cross-domain redirects
  getClientID() {
    try {
      // Access the global 'ga' object directly via window.
      // If 'ga' is expected to be available, ensure the Google Analytics script is loaded before this runs.
      const tracker = window.ga.getAll()[0]; // Use const and window.ga
      return tracker.get('clientId');
    } catch (e) {
      console.log("Error fetching clientId:", e.message); // Log error message for better debugging
      return "n/a";
    }
  }

  // E-commerce tracking in GTM to analyze purchase activity
  ecomTracking(cart) {
    window.dataLayer = window.dataLayer || [];
    const transactionProducts = cart.items.map(item => ({ // Use map for cleaner array transformation
      sku: String(item.itemId), // Ensure string type explicitly
      name: String(item.itemId), // Ensure string type explicitly
      price: parseInt(item.totalCost, 10),
      quantity: parseInt(item.quantity, 10)
    }));

    window.dataLayer.push({
      transactionId: cart.cartId,
      transactionAffiliation: cart.affiliateId,
      transactionTotal: cart.total,
      transactionTax: cart.tax,
      transactionProducts: transactionProducts,
      event: 'Report Ecommerce'
    });
  }

  // Set cookie in browser
  setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    const expires = `expires=${d.toUTCString()}`; // Use const and template literal
    document.cookie = `${cname}=${cvalue};${expires};path=/`; // Use template literal
  }

  /**
   * Get the value of a cookie
   * @param  {String} name  The name of the cookie
   * @return {String}       The cookie value
   */
  getCookie(name) {
    const value = `; ${document.cookie}`; // Use const
    const parts = value.split(`; ${name}=`); // Use const and template literal
    if (parts.length === 2) return parts.pop().split(';').shift();
  }

}

export { AppService };